import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { zLayers } from '../../../styles/theme';
import { updateCourseGroupAdminNotes } from '../../../api/courseGroupPortalApi';

/**
 * Long-lived notes on a course group, for campus staff only (CourseGroupPortal.adminNotes).
 * Unlike a lesson's own notes they apply to every week and stay through a term rollover.
 *
 * Works like a comment in a spreadsheet: hover to read, click the bubble to edit in place.
 * The bubble stays open while the pointer travels from the class onto it, and once editing
 * starts only Save, Cancel, Esc or a click elsewhere (which also saves) will close it.
 *
 * Which campuses have this is CampusPortal.courseGroupAdminNotes. The API checks it as well.
 */

export const ADMIN_NOTES_MAX_LENGTH = 1000;

const SHOW_DELAY_MS = 80;
const HIDE_GRACE_MS = 220;
const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 6;
const BUTTON_MARKER = 'data-admin-notes-button';

export const campusHasAdminNotes = (campus) => campus?.courseGroupAdminNotes === true;

/** Open/close timing for one bubble. `open` is what to show, or null. */
export const useAdminNoteHover = () => {
  const [open, setOpen] = useState(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const editing = useRef(false);

  const clearTimers = useCallback(() => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const show = useCallback((payload) => {
    if (editing.current) return;
    clearTimers();
    showTimer.current = setTimeout(() => setOpen(payload), SHOW_DELAY_MS);
  }, [clearTimers]);

  const hide = useCallback(() => {
    clearTimeout(showTimer.current);
    if (editing.current) return;
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setOpen(null), HIDE_GRACE_MS);
  }, []);

  const keep = useCallback(() => clearTimeout(hideTimer.current), []);

  // Hide right away unless someone is typing — for when a class is picked up or opened.
  const dismiss = useCallback(() => {
    clearTimers();
    if (!editing.current) setOpen(null);
  }, [clearTimers]);

  const close = useCallback(() => {
    clearTimers();
    editing.current = false;
    setOpen(null);
  }, [clearTimers]);

  const edit = useCallback((payload) => {
    if (editing.current) return;
    clearTimers();
    editing.current = true;
    setOpen({ ...payload, startEditing: true });
  }, [clearTimers]);

  const setEditing = useCallback((value) => { editing.current = value; }, []);

  return { open, show, hide, keep, dismiss, close, edit, setEditing };
};

/**
 * @param {Object}   open      { rect, courseGroupId, title, note, startEditing }
 * @param {Object}   hover     the object returned by useAdminNoteHover
 * @param {boolean}  canEdit
 * @param {Function} onSaved   called with the saved text
 */
export const AdminNotesBubble = ({ open, hover, canEdit = true, onSaved }) => {
  const { rect, courseGroupId, title, note, startEditing } = open;
  const savedNote = note || '';
  const [editing, setEditing] = useState(Boolean(startEditing));
  const [draft, setDraft] = useState(savedNote);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);
  const bubbleRef = useRef(null);
  const textareaRef = useRef(null);
  const savingRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Beside the class if it fits on either side, otherwise above or below it.
  useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left;
    let top = rect.top;
    if (rect.right + ANCHOR_GAP + width + VIEWPORT_MARGIN <= vw) {
      left = rect.right + ANCHOR_GAP;
    } else if (rect.left - ANCHOR_GAP - width >= VIEWPORT_MARGIN) {
      left = rect.left - ANCHOR_GAP - width;
    } else {
      left = rect.left;
      top = rect.bottom + ANCHOR_GAP + height + VIEWPORT_MARGIN <= vh
        ? rect.bottom + ANCHOR_GAP
        : rect.top - ANCHOR_GAP - height;
    }
    setPosition({
      left: Math.max(VIEWPORT_MARGIN, Math.min(left, vw - width - VIEWPORT_MARGIN)),
      top: Math.max(VIEWPORT_MARGIN, Math.min(top, vh - height - VIEWPORT_MARGIN))
    });
  }, [rect, editing, savedNote, error]);

  // Waits for the position: the bubble is hidden until it has one, and a hidden field cannot
  // take focus, which would leave a first note needing an extra click before typing.
  const positioned = position !== null;
  useEffect(() => {
    if (editing && positioned && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editing, positioned]);

  const commit = async () => {
    if (savingRef.current) return;
    const next = draft.replace(/\s+$/, '');
    if (next === savedNote) {
      hover.close();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await updateCourseGroupAdminNotes(courseGroupId, next);
      if (onSaved) onSaved(saved);
      hover.close();
    } catch (e) {
      const message = e.message || 'Could not save the note';
      savingRef.current = false;
      // Whatever was underneath may have closed already (Close on the lesson dialog also
      // counts as a click elsewhere), and then this is the only way to report the failure.
      if (mounted.current) {
        setSaving(false);
        setError(message);
      } else {
        window.alert(`Admin Notes were not saved: ${message}`);
      }
    }
  };

  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (!editing) return undefined;
    const onMouseDown = (event) => {
      if (bubbleRef.current && bubbleRef.current.contains(event.target)) return;
      if (event.target.closest && event.target.closest(`[${BUTTON_MARKER}]`)) return;
      commitRef.current();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editing]);

  // A read-only bubble would otherwise stay put while the class scrolls out from under it.
  useEffect(() => {
    if (editing) return undefined;
    const onScroll = (event) => {
      if (bubbleRef.current && event.target instanceof Node && bubbleRef.current.contains(event.target)) return;
      hover.dismiss();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [editing, hover]);

  const startEdit = () => {
    if (!canEdit || editing) return;
    hover.setEditing(true);
    setDraft(savedNote);
    setEditing(true);
  };

  const stop = (event) => event.stopPropagation();

  return createPortal(
    <Bubble
      ref={bubbleRef}
      role="dialog"
      aria-label="Admin Notes"
      $editing={editing}
      style={position
        ? { left: position.left, top: position.top }
        : { left: 0, top: 0, visibility: 'hidden' }}
      onMouseEnter={hover.keep}
      onMouseLeave={hover.hide}
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={(event) => { stop(event); startEdit(); }}
      onKeyDown={(event) => {
        stop(event);
        if (event.key === 'Escape') hover.close();
      }}
    >
      <BubbleTitle>Admin Notes{title ? ` · ${title}` : ''}</BubbleTitle>
      {editing ? (
        <>
          <BubbleTextarea
            ref={textareaRef}
            value={draft}
            maxLength={ADMIN_NOTES_MAX_LENGTH}
            rows={7}
            placeholder="Only staff can see this. Teachers and parents cannot."
            onChange={(event) => setDraft(event.target.value)}
            disabled={saving}
          />
          {error && <BubbleError>{error}</BubbleError>}
          <BubbleActions>
            <small>{draft.length}/{ADMIN_NOTES_MAX_LENGTH}</small>
            <BubbleButton type="button" onClick={() => hover.close()} disabled={saving}>
              Cancel
            </BubbleButton>
            <BubbleButton type="button" $primary onClick={commit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </BubbleButton>
          </BubbleActions>
        </>
      ) : (
        <BubbleText $canEdit={canEdit} title={canEdit ? 'Click to edit' : undefined}>
          {savedNote}
        </BubbleText>
      )}
    </Bubble>,
    document.body
  );
};

/** The footer button on the lesson and course group dialogs, and the way to add a first note. */
export const AdminNotesButton = ({
  courseGroupId, title, note, onSaved, Button, buttonProps = {}
}) => {
  const hover = useAdminNoteHover();
  const payloadFor = (event) => ({
    rect: event.currentTarget.getBoundingClientRect(),
    courseGroupId,
    title,
    note
  });

  return (
    <>
      <Button
        type="button"
        {...{ [BUTTON_MARKER]: true }}
        {...buttonProps}
        onMouseEnter={(event) => { if (note) hover.show(payloadFor(event)); }}
        onMouseLeave={hover.hide}
        onClick={(event) => hover.edit(payloadFor(event))}
      >
        Admin Notes{note ? ' ●' : ''}
      </Button>
      {hover.open && (
        <AdminNotesBubble
          // Clicking the button while the read-only bubble is up has to start over in edit mode.
          key={`${courseGroupId}-${hover.open.startEditing ? 'edit' : 'read'}`}
          open={hover.open}
          hover={hover}
          onSaved={onSaved}
        />
      )}
    </>
  );
};

const Bubble = styled.div`
  position: fixed;
  z-index: ${zLayers.popover};
  width: ${props => (props.$editing ? '340px' : '280px')};
  max-width: calc(100vw - 16px);
  background: #fffbeb;
  border: 1px solid #f1af59;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 10px 12px;
  color: #1f2937;
  font-size: 13px;
  line-height: 1.45;
  text-align: left;
`;

const BubbleTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BubbleText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 280px;
  overflow-y: auto;
  cursor: ${props => (props.$canEdit ? 'text' : 'default')};
`;

const BubbleTextarea = styled.textarea`
  display: block;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 8px;
  font: inherit;
  color: inherit;
  background: white;
  resize: vertical;

  &:focus {
    outline: 2px solid #f1af59;
    outline-offset: -1px;
  }
`;

const BubbleError = styled.div`
  margin-top: 6px;
  color: #b91c1c;
  font-size: 12px;
`;

const BubbleActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;

  small {
    margin-right: auto;
    color: #6b7280;
    font-size: 11px;
  }
`;

const BubbleButton = styled.button`
  border: 1px solid ${props => (props.$primary ? '#f1af59' : '#d1d5db')};
  background: ${props => (props.$primary ? '#f1af59' : 'white')};
  color: ${props => (props.$primary ? 'white' : '#374151')};
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
